import { useState, useEffect, useCallback, forwardRef } from 'react';
import { Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../lib/utils';
import { Input } from '../../../components/ui/input';

interface SearchInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value' | 'size'> {
  value?: string;
  onChange?: (value: string) => void;
  debounceMs?: number;
  /** "default" = bordered input, "pill" = rounded filled bar (Gmail-style) */
  variant?: 'default' | 'pill';
}

/**
 * Styled search input with search icon and optional debounce.
 */
export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  (
    {
      value: externalValue = '',
      onChange,
      debounceMs = 300,
      placeholder,
      className,
      variant = 'default',
      ...props
    },
    ref
  ) => {
    const { t } = useTranslation('common');
    const [internalValue, setInternalValue] = useState(externalValue);

    // Sync internal value when external value changes
    useEffect(() => {
      setInternalValue(externalValue);
    }, [externalValue]);

    // Debounced onChange callback
    useEffect(() => {
      if (!onChange) return;

      const trimmed = internalValue.trim();
      const timer = setTimeout(() => {
        if (trimmed !== externalValue) {
          onChange(trimmed);
        }
      }, debounceMs);

      return () => clearTimeout(timer);
    }, [internalValue, externalValue, onChange, debounceMs]);

    const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
      setInternalValue(e.target.value);
    }, []);

    const isPill = variant === 'pill';

    return (
      <div className={cn('relative w-full sm:w-72', className)}>
        <Search className={cn(
          'absolute top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground',
          isPill ? 'left-4' : 'left-3',
        )} />
        <Input
          ref={ref}
          value={internalValue}
          onChange={handleChange}
          placeholder={placeholder || t('app.search')}
          aria-label={placeholder || t('app.search')}
          data-testid="search-input"
          className={cn(
            isPill
              ? 'ps-11 pe-4 h-11 rounded-full border-none bg-muted/60 hover:bg-muted focus-visible:bg-background focus-visible:ring-1 focus-visible:ring-ring focus-visible:shadow-sm transition-colors'
              : 'ps-9',
          )}
          {...props}
        />
      </div>
    );
  }
);

SearchInput.displayName = 'SearchInput';
